import { RATES } from '../../constants/Entities';
import { getLiteral } from 'utils/getLiteral';
import Context from 'managers/Context';

import { RatesModel } from '../../models';

export default class RatesManager {
    constructor() {}

    shouldOpenDetail = () => false;

    getSchema = () => [
        {
            show: true,
            columns: 2,
            fields: [
                {
                    id: 'ratesGeneralInfoHeader',
                    isCustom: true,
                },
                {
                    id: 'name',
                    type: 'text',
                    mandatory: true,
                    label: getLiteral('label_name_rate'),
                    labelMode: 'vertical',
                    column: 1,
                    inputAttrs: { isFullWidth: true },
                },
                {
                    id: 'description',
                    type: 'text',
                    label: getLiteral('label_description'),
                    labelMode: 'vertical',
                    column: 1,
                    inputAttrs: { isFullWidth: true },
                },
                {
                    id: 'idCurrency',
                    type: 'singleValueList',
                    mandatory: true,
                    label: getLiteral('label_currency'),
                    labelMode: 'vertical',
                    column: 1,
                    inputAttrs: { list: 'tblCurrency', isFullWidth: true },
                },
                {
                    id: 'startDate',
                    type: 'date',
                    label: getLiteral('label_start_date'),
                    labelMode: 'vertical',
                    column: 2,
                    inputAttrs: { isFullWidth: true },
                },
                {
                    id: 'endDate',
                    type: 'date',
                    label: getLiteral('label_end_date'),
                    labelMode: 'vertical',
                    column: 2,
                    inputAttrs: { isFullWidth: true },
                },
                {
                    id: 'strIdEnvironment',
                    type: 'multipleValueList',
                    isCustom: true,
                    label: getLiteral('label_environment'),
                    labelMode: 'vertical',
                    column: 2,
                },
            ],
        },
        {
            title: getLiteral('label_apply_discounts_rates'),
            show: true,
            fields: [
                {
                    id: 'ratesDiscountsSectionHeader',
                    isCustom: true,
                },
                {
                    id: 'familyrates',
                    isCustom: true,
                },
                {
                    id: 'productrates',
                    isCustom: true,
                },
                {
                    id: 'companyrates',
                    isCustom: true,
                },
            ],
        },
    ];

    prepareDataForCrud = (data) => {
        if (!data) return data;
        if (data instanceof RatesModel) {
            return RatesModel.toEntityCrudData(data);
        }
        return data;
    };

    afterGetEntity = (data) => Promise.resolve(this.prepareDataForCrud(data));

    getSpecificEntityDefaultValues = (finalData, id) => {
        if (id) return finalData;

        const config = Context.cacheManager.getConfigStore();
        return {
            ...finalData,
            productrates: finalData.productrates || [],
            familyrates: finalData.familyrates || [],
            companyrates: finalData.companyrates || [],
            currencysymbol: config.userData.currencySymbol,
        };
    };

    processForServer = (schema, data) => RatesModel.fromEntityCrudData(data).saveCrud;

    saveEntityCrud = ({ data, serverData, resolve, reject }) => {
        const crudPayload = serverData ?? RatesModel.fromEntityCrudData(data).saveCrud;
        const onSuccess = (result) => {
            Context.serverListManager
                .getRatesServerList()
                .then((ratesList) => {
                    this.context.cacheManager.setServerList('ratesValueList', ratesList);
                })
                .then(() => resolve(result ?? {}))
                .catch((error) => reject({ serverError: error?.error || error || true }));
        };
        const onError = (error) => {
            reject({ serverError: error?.error || error || true });
        };

        if (data.id) {
            this.context.domainManager.updateEntity(
                RATES.entity,
                data.id,
                crudPayload,
                onSuccess,
                onError,
            );
        } else {
            this.context.domainManager.createEntity(RATES.entity, crudPayload, onSuccess, onError);
        }
    };

    deleteEntityCrud = ({ data, resolve, reject }) => {
        if (!data?.id) {
            reject({ serverError: true });
            return;
        }

        this.context.domainManager.deleteEntity(
            RATES.entity,
            data.id,
            () => {
                Context.serverListManager
                    .getRatesServerList()
                    .then((ratesList) => {
                        this.context.cacheManager.setServerList('ratesValueList', ratesList);
                    })
                    .then(() => resolve(data))
                    .catch((error) => reject({ serverError: error?.error || error || true }));
            },
            (error) => {
                reject({ serverError: error?.error || error || true });
            },
        );
    };

    checkCustomErrors = (errors, schema, data) => {
        const nextErrors = { ...errors };
        const ratesModel = RatesModel.fromEntityCrudData(data);

        const validateLines = (lines, fieldId, discountFieldId) => {
            const rowErrors = {};
            lines.forEach((model, index) => {
                const discountValue = model.entity?.[discountFieldId];
                if (discountValue === null || discountValue === undefined || discountValue === '') {
                    rowErrors[index] = { [discountFieldId]: 'mandatory' };
                }
            });

            if (Object.keys(rowErrors).length > 0) {
                nextErrors[fieldId] = { rows: rowErrors };
            }
        };

        validateLines(ratesModel.productRates, 'productrates', 'ratediscount1');
        validateLines(ratesModel.familyRates, 'familyrates', 'ratediscount1');

        return nextErrors;
    };

    getRates(offset, pageSize, filterSearch, successCallback, errorCallback) {
        this.context.domainManager.getRates(
            offset,
            pageSize,
            filterSearch,
            (data) => {
                if (data.State === '1') {
                    successCallback(data.Result, data.TotalRows);
                } else {
                    errorCallback();
                }
            },
            errorCallback,
        );
    }

    getRatesAll(success, error) {
        const offset = 0;
        const pageSize = 500;
        const filterSearch = '';
        this.getRates(
            offset,
            pageSize,
            filterSearch,
            (data) => {
                success(data);
            },
            error,
        );
    }

    getEntity(id, success, error) {
        const entity = new Promise((resolve, reject) => {
            this.getRate(id, resolve, reject);
        });

        const productRates = new Promise((resolve, reject) => {
            this.getProductsRates(id, resolve, reject);
        });

        const familyRates = new Promise((resolve, reject) => {
            this.getFamilyRates(id, resolve, reject);
        });

        const companyRates = new Promise((resolve, reject) => {
            this.getCompanyRates(id, resolve, reject);
        });

        Promise.all([entity, productRates, familyRates, companyRates]).then((result) => {
            success(new RatesModel(result[0], result[1], result[2], result[3]));
        }, error);
    }

    getRate(id, success, error) {
        this.context.domainManager.getEntity(
            id,
            RATES.entity,
            true,
            (data) => {
                success(data);
            },
            error,
        );
    }

    getProductsRates(id, success, error) {
        this.context.domainManager.getProductsRates(
            id,
            (data) => {
                success(data.Result);
            },
            error,
        );
    }

    getFamilyRates(id, success, error) {
        this.context.domainManager.getFamilyRates(
            id,
            (data) => {
                success(data.Result);
            },
            error,
        );
    }

    getCompanyRates(id, success, error) {
        this.context.domainManager.getCompanyRates(
            id,
            (data) => {
                success(data.Result);
            },
            error,
        );
    }

    transformDataForEntityList = (backendData) => {
        let totalRows = backendData.TotalRows;
        if (backendData.State === '1' && totalRows === -1) {
            totalRows = 0;
        }
        return {
            Data: backendData.Result,
            MetaData: { totalRows: totalRows },
        };
    };
}
